import {
  addDays,
  DIFFICULTY_LABELS,
  formatMinutes,
  formatPercent,
  formatSigned,
  isoWeekKey,
  isoWeekStart,
  monthKey,
} from '@algo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ConsistencyCalendar, GrowthChart, TopicMasteryBars } from '../components/charts';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Spinner,
} from '../components/ui';
import { api, qk } from '../lib/api';

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-edge bg-raised p-3">
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 font-mono text-xl tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted">{sub}</p>}
    </div>
  );
}

/** Shifts an ISO week key by whole weeks. */
function shiftWeek(period: string, weeks: number): string {
  return isoWeekKey(addDays(isoWeekStart(period), weeks * 7));
}

/** Shifts a `YYYY-MM` key by whole months. */
function shiftMonth(period: string, months: number): string {
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(5, 7));
  const shifted = new Date(Date.UTC(year, month - 1 + months, 1));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function StatisticsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [week, setWeek] = useState(() => isoWeekKey(today));
  const [month, setMonth] = useState(() => monthKey(today));

  const weekly = useQuery({
    queryKey: qk.weekly(week),
    queryFn: () => api.weeklyReport(week),
  });
  const monthly = useQuery({
    queryKey: qk.monthly(month),
    queryFn: () => api.monthlyReport(month),
  });
  const dashboard = useQuery({ queryKey: qk.dashboard, queryFn: () => api.getDashboard() });

  if (weekly.error) return <ErrorState error={weekly.error} onRetry={() => void weekly.refetch()} />;

  return (
    <>
      <PageHeader
        title="Статистик"
        description="Бүх тоо анхдагч өгөгдлөөс шууд тооцоологддог; snapshot нь зөвхөн кэш."
      />

      <Card
        title={`Долоо хоногийн тайлан — ${week}`}
        subtitle={
          weekly.data ? `${weekly.data.startDay} → ${weekly.data.endDay}` : 'Ачаалж байна…'
        }
        action={
          <div className="flex gap-1">
            <Button onClick={() => setWeek(shiftWeek(week, -1))}>‹</Button>
            <Button onClick={() => setWeek(isoWeekKey(today))}>Энэ долоо хоног</Button>
            <Button
              onClick={() => setWeek(shiftWeek(week, 1))}
              disabled={week >= isoWeekKey(today)}
            >
              ›
            </Button>
          </div>
        }
      >
        {weekly.isLoading || !weekly.data ? (
          <Spinner />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <Metric
                label="Рэйтингийн өөрчлөлт"
                value={formatSigned(weekly.data.ratingChange)}
                sub={`${weekly.data.ratingAtStart} → ${weekly.data.ratingAtEnd} (${formatPercent(
                  weekly.data.ratingChangePercent,
                )})`}
              />
              <Metric label="Бодсон бодлого" value={String(weekly.data.problemsSolved)} />
              <Metric label="Судалсан хугацаа" value={formatMinutes(weekly.data.minutesStudied)} />
              <Metric label="Идэвхтэй хоног" value={`${weekly.data.activeDays}/7`} />
              <Metric
                label="Хамгийн их ахисан"
                value={weekly.data.mostImprovedTopic?.name ?? '—'}
                sub={
                  weekly.data.mostImprovedTopic
                    ? `+${weekly.data.mostImprovedTopic.pointsGained} оноо`
                    : 'Энэ долоо хоногт бодолт алга'
                }
              />
            </div>

            {weekly.data.weakestArea && (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
                <Badge tone="warning">Хамгийн сул хэсэг</Badge>
                <span>{weekly.data.weakestArea.name}</span>
                <span className="text-muted">— {weekly.data.weakestArea.reason}</span>
              </div>
            )}

            <div className="mt-5">
              <p className="mb-2 text-xs uppercase tracking-wide text-muted">Өдөр тутам</p>
              <div className="grid grid-cols-7 gap-2">
                {weekly.data.perDay.map((day) => (
                  <div
                    key={day.day}
                    className="rounded-lg border border-edge bg-raised p-2 text-center"
                  >
                    <p className="text-xs text-muted">{day.day.slice(5)}</p>
                    <p className="mt-1 font-mono text-sm">{day.problemsSolved}</p>
                    <p className="text-[11px] text-muted">{day.minutes}м</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </Card>

      <Card
        className="mt-4"
        title={`Сарын тайлан — ${month}`}
        subtitle={monthly.data ? `${monthly.data.startDay} → ${monthly.data.endDay}` : 'Ачаалж байна…'}
        action={
          <div className="flex gap-1">
            <Button onClick={() => setMonth(shiftMonth(month, -1))}>‹</Button>
            <Button onClick={() => setMonth(monthKey(today))}>Энэ сар</Button>
            <Button
              onClick={() => setMonth(shiftMonth(month, 1))}
              disabled={month >= monthKey(today)}
            >
              ›
            </Button>
          </div>
        }
      >
        {monthly.isLoading || !monthly.data ? (
          <Spinner />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Metric label="Бодсон бодлого" value={String(monthly.data.problemsSolved)} />
              <Metric label="Судалсан хугацаа" value={formatMinutes(monthly.data.minutesStudied)} />
              <Metric label="Идэвхтэй хоног" value={String(monthly.data.activeDays)} />
              <Metric
                label="Дундаж хүндрэл"
                value={
                  monthly.data.averageDifficultyLabel
                    ? DIFFICULTY_LABELS[monthly.data.averageDifficultyLabel]
                    : '—'
                }
                sub={`оноо ${monthly.data.averageDifficultyScore} / 4`}
              />
            </div>

            <div className="mt-5 grid gap-4 xl:grid-cols-2">
              <div>
                <p className="mb-2 text-xs uppercase tracking-wide text-muted">Рэйтингийн өсөлт</p>
                <GrowthChart data={monthly.data.growth} />
              </div>
              <div>
                <p className="mb-2 text-xs uppercase tracking-wide text-muted">
                  Хамгийн их дасгал хийсэн сэдвүүд
                </p>
                {monthly.data.mostPracticedTopics.length === 0 ? (
                  <EmptyState title="Энэ сард юу ч бүртгэгдээгүй" />
                ) : (
                  <ul className="space-y-1.5">
                    {monthly.data.mostPracticedTopics.map((topic) => (
                      <li
                        key={topic.topicId}
                        className="flex items-center justify-between gap-3 rounded-lg border border-edge bg-raised px-3 py-2 text-sm"
                      >
                        <span className="truncate">{topic.name}</span>
                        <span className="shrink-0 font-mono text-xs text-muted">
                          {formatMinutes(topic.minutes)} · {topic.problemsSolved} бодлого
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}
      </Card>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <Card title="Сэдвийн эзэмшилт" subtitle="Төлөв, итгэл, хэмжээг нэгтгэсэн">
          {dashboard.data ? <TopicMasteryBars data={dashboard.data.topicMastery} /> : <Spinner />}
        </Card>
        <Card title="Тогтвортой байдлын хуанли" subtitle="Сүүлийн 365 хоног">
          {dashboard.data ? <ConsistencyCalendar days={dashboard.data.heatmap} /> : <Spinner />}
        </Card>
      </div>
    </>
  );
}
